import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../shared/BrandLogo';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import AppStoreButtons from '../shared/AppStoreButtons';
import { CREATE_GIFT_PAGE_URL } from '../../constants/appLinks';
import { useScrollToSection } from '../../hooks/useScrollToSection';
import '../../styles/landing/BrandLogo.css';
import '../../styles/landing/LandingHeader.css';

const LandingHeader: React.FC = () => {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { handleNavClick } = useScrollToSection();

    useEffect(() => {
        const onScroll = () => setScrolled(window.pageYOffset > 24);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const [isMobileNav, setIsMobileNav] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia('(max-width: 960px)').matches : false
    );

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 960px)');
        const onChange = () => setIsMobileNav(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (!menuOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [menuOpen]);

    const navLinks = [
        { href: '#how-it-works', label: t('nav.howItWorks') },
        { href: '#couples', label: t('nav.forCouples') },
        { href: '#guests', label: t('nav.forGuests') },
        { href: '#security', label: t('nav.security') },
        { href: '#faq', label: t('nav.faq') },
    ];

    return (
        <header className={`landing-header ${scrolled ? 'landing-header--scrolled' : ''}`}>
            <nav className="landing-header__nav landing-container" aria-label="Main navigation">
                <a
                    href="#home"
                    className="landing-header__logo"
                    onClick={(e) => {
                        handleNavClick(e, '#home');
                        setMenuOpen(false);
                    }}
                >
                    <BrandLogo
                        size={32}
                        wordmarkClassName="landing-header__logo-text"
                    />
                </a>

                <button
                    type="button"
                    className="landing-header__toggle"
                    aria-expanded={menuOpen}
                    aria-controls="landing-header-menu"
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    <span className="landing-header__toggle-bar" />
                    <span className="landing-header__toggle-bar" />
                    <span className="landing-header__toggle-bar" />
                </button>

                <div
                    id="landing-header-menu"
                    className={`landing-header__panel ${menuOpen ? 'is-open' : ''}`}
                    {...(isMobileNav && !menuOpen ? { inert: true } : {})}
                >
                    <ul className="landing-header__links">
                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={(e) => {
                                        handleNavClick(e, link.href);
                                        setMenuOpen(false);
                                    }}
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                    <div className="landing-header__actions">
                        <LanguageSwitcher />
                        <div className="landing-header__stores-desktop">
                            <AppStoreButtons variant="compact" />
                        </div>
                        <a
                            href={CREATE_GIFT_PAGE_URL}
                            className="btn btn--primary landing-header__cta"
                        >
                            {t('cta.createGiftPage')}
                        </a>
                    </div>
                </div>
            </nav>
            {menuOpen && (
                <button
                    type="button"
                    className="landing-header__backdrop"
                    aria-label="Close menu"
                    onClick={() => setMenuOpen(false)}
                />
            )}
        </header>
    );
};

export default LandingHeader;
